import axios from "axios";
import { config } from "../config";
import { userService } from "./UserService";

/** @typedef {import('../components/custom/10x/viewport/logTypes').LogEvent} LogEvent */
/** @typedef {import('../components/custom/10x/viewport/logTypes').LogPoller} LogPoller */


class TenXService {
    getRequestHeaders() {
        const authToken = userService.getAuthToken();
        if (!authToken) {
            throw new Error("Authentication token is missing");
        }

        return {
            Authorization: `${authToken}`,
            "Content-Type": "application/json",
        };
    }

    /**
     * Post the user's token to the API's /docs/login endpoint and open the
     * interactive API docs in a new tab.
     */
    openApiDocs() {
        const authToken = userService.getAuthToken();
        if (!authToken) {
            throw new Error("Authentication token is missing");
        }
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${apiUrl}/docs/login`;
        form.target = "_blank";

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "jwt";
        input.value = authToken;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
        form.remove();
    }

    /**
     * Copy the user's JWT to the clipboard, for calling the API directly with their own credentials.
     * @returns {Promise<void>}
     */
    async copyAuthToken() {
        const authToken = userService.getAuthToken();
        if (!authToken) {
            throw new Error("Authentication token is missing");
        }
        await navigator.clipboard.writeText(authToken);
    }

    /**
     * Get all devices and their most recent image snapshot.
     * @returns {Promise} Device list
     */
    async getDevices() {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(`${apiUrl}/v1/devices`, {
                headers: this.getRequestHeaders(),
            });
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get image for a specific device
     * @param {string} deviceName - The device name
     * @param {boolean} secondary - Whether to fetch the secondary camera image
     * @returns {Promise<Blob>}
     */
    async getDeviceImage(deviceName, secondary = false) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");
        const endpoint = secondary ? 'imageSecondary' : 'image';

        try {
            const result = await axios.get(`${apiUrl}/v1/devices/${deviceName}/${endpoint}`, {
                responseType: 'blob',
                headers: this.getRequestHeaders(),
            });
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get image metadata for a specific device
     * @param {string} deviceName - The device name
     * @param {boolean} secondary - Whether to fetch the secondary camera image metadata
     * @returns {Promise} Most recent image metadata record
     */
    async getDeviceImageMetadata(deviceName, secondary = false) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");
        const endpoint = secondary ? 'imageMetadataSecondary' : 'imageMetadata';

        try {
            const result = await axios.get(`${apiUrl}/v1/devices/${deviceName}/${endpoint}`, {
                headers: this.getRequestHeaders(),
            });
            // Older API returns a single object (404 when none); newer API returns a
            // page { items, nextToken } with items ordered oldest to newest (empty when
            // none). With no range params the page holds just the latest reading.
            // Normalize to the newest object.
            if (!("items" in result.data)) return result.data;
            const { items } = result.data;
            if (items.length === 0) {
                throw new Error(`No image metadata found for ${deviceName}`);
            }
            return items[items.length - 1];
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get device logs from a starting point. Omit startTime to default to the past
     * 10 minutes on the backend — use this for live view start and explorer start.
     * @param {string} deviceName
     * @param {number} [startTime] - Unix milliseconds
     * @returns {Promise<LogPoller>}
     */
    async getDeviceLogs(deviceName, startTime = null) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");
        const params = startTime ? `?start_time=${startTime}` : "";

        try {
            const result = await axios.get(
                `${apiUrl}/v1/devices/${deviceName}/logs${params}`,
                { headers: this.getRequestHeaders(), timeout: 15000 }
            );
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Continue fetching device logs from a cursor position returned by a prior call.
     * Used for live view polling and explorer pagination.
     * @param {string} deviceName
     * @param {string} nextToken
     * @returns {Promise<LogPoller>}
     */
    async getNextDeviceLogs(deviceName, nextToken) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(
                `${apiUrl}/v1/devices/${deviceName}/logs?next_token=${nextToken}`,
                { headers: this.getRequestHeaders(), timeout: 15000 }
            );
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get the current Mender connection status of a device.
     * @param {string} deviceName
     * @returns {Promise<"connected"|"disconnected"|"unknown">}
     */
    async getDeviceStatus(deviceName) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(`${apiUrl}/v1/devices/${deviceName}/status`, {
                headers: this.getRequestHeaders(),
            });
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get the current firmware version string of a device.
     * @param {string} deviceName
     * @returns {Promise<string>}
     */
    async getDeviceVersion(deviceName) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(`${apiUrl}/v1/devices/${deviceName}/version`, {
                headers: this.getRequestHeaders(),
            });
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get a device's last deployed configuration (both the intended `configured`
     * values and the device-`reported` actual values).
     * @param {string} deviceName
     * @returns {Promise} DeviceConfiguration
     */
    async getDeviceConfiguration(deviceName) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(
                `${apiUrl}/v1/devices/${deviceName}/configuration`,
                { headers: this.getRequestHeaders() }
            );
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get the JSON Schema that matches the device's firmware version. Used to
     * auto-render the configuration form.
     * @param {string} deviceName
     * @returns {Promise} JSON Schema object
     */
    async getDeviceConfigurationSchema(deviceName) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(
                `${apiUrl}/v1/devices/${deviceName}/configurationSchema`,
                { headers: this.getRequestHeaders() }
            );
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get the 5 most recent configuration deployments for a device.
     * @param {string} deviceName
     * @returns {Promise<Array>}
     */
    async getRecentConfigurationDeployments(deviceName) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(
                `${apiUrl}/v1/devices/${deviceName}/listConfigurationDeployments`,
                { headers: this.getRequestHeaders() }
            );
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Validate, stage, and deploy a configuration to a device. Returns the Mender
     * deployment id, which confirms Mender accepted the deployment.
     * @param {string} deviceName
     * @param {Object} parameterList - The configuration parameters to deploy
     * @param {boolean} overwrite - Replace the full configuration instead of merging
     * @returns {Promise<string>} deployment id
     */
    async deployDeviceConfiguration(deviceName, parameterList, overwrite = false) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.post(
                `${apiUrl}/v1/devices/${deviceName}/configuration`,
                {
                    parameter_list: parameterList,
                    overwrite_existing_configuration: overwrite,
                },
                { headers: this.getRequestHeaders() }
            );
            return result.data;
        } catch (error) {
            console.log("error", error);
            throw error;
        }
    }

    /**
     * Get the Mender deployment log for a device in a deployment. Mender only
     * stores a log when the device reported one (typically on failure), so the
     * API returns 404 when none exists.
     * @param {string} deviceName
     * @param {string} deploymentId
     * @returns {Promise<string>} the raw deployment log text
     */
    async getConfigurationDeploymentLog(deviceName, deploymentId) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.get(
                `${apiUrl}/v1/devices/${deviceName}/deployments/${deploymentId}/log`,
                { headers: this.getRequestHeaders(), responseType: "text" }
            );
            return result.data;
        } catch (error) {
            // A 404 means the device never recorded a log for this deployment
            // (typical on success); surface that as null rather than an error.
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return null;
            }
            console.log("error", error);
            throw error;
        }
    }


    /**
     * Trigger an on-device image capture from both cameras. The device captures
     * primary + secondary frames and uploads them to S3 via its normal pipeline;
     * the new frames become servable through the image endpoints shortly after.
     * The backend holds a remote-terminal session open for this, so it can take
     * several seconds. Requires the device to be online.
     * @param {string} deviceName
     * @returns {Promise<string>} raw command output from the device
     */
    async captureDeviceImages(deviceName) {
        const apiUrl = config.getApiUrl("tenxDeviceAdmin");

        try {
            const result = await axios.post(
                `${apiUrl}/v1/devices/${deviceName}/captureImages`,
                null,
                { headers: this.getRequestHeaders(), timeout: 60000 }
            );
            return result.data;
        } catch (error) {
            // Surface Backend error detail
            const detail = error?.response?.data?.detail;
            console.error("captureDeviceImages failed", detail || error);
            throw new Error(detail || error.message || "Failed to trigger image capture");
        }
    }
    
}

export const tenXService = new TenXService();
